import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MapPin, Clock, ShoppingBag, ArrowRight, Car, HandHelping, Wifi, Accessibility, Baby,
    PackageSearch, Banknote, Zap, Sparkles, HeartPulse, Navigation, Calendar, Ticket, Film,
    X, Layers, ShieldCheck, UtensilsCrossed, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useMall } from '@/lib/mall';
import { useAuth } from '@/lib/auth';
import { proxyImg } from '@/lib/imageProxy';
import BrandLogo from '@/components/BrandLogo';

const ICONS = { Car, HandHelping, Wifi, Accessibility, Baby, PackageSearch, Banknote, Zap, Sparkles, HeartPulse };

const COLORS = {
    yellow: 'bg-inorbit-yellow text-inorbit-blue',
    orange: 'bg-inorbit-orange text-white',
    blue: 'bg-inorbit-blue text-white',
};

export default function MallHub() {
    const { slug } = useParams();
    const { choose } = useMall();
    const { type } = useAuth();
    const [mall, setMall] = useState(null);
    const [products, setProducts] = useState([]);
    const [brands, setBrands] = useState([]);
    const [offers, setOffers] = useState([]);
    const [events, setEvents] = useState([]);
    const [services, setServices] = useState([]);
    const [parking, setParking] = useState(null);
    const [floors, setFloors] = useState([]);
    const [movies, setMovies] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [parkingModal, setParkingModal] = useState(false);
    const [movieModal, setMovieModal] = useState(null);
    const [restaurantModal, setRestaurantModal] = useState(null);

    useEffect(() => {
        choose(slug);
        api.get(`/malls/${slug}`).then(({ data }) => setMall(data));
        api.get(`/catalog/products?mall_slug=${slug}&page_size=8`).then(({ data }) => setProducts(data.items || [])).catch(() => {});
        api.get(`/malls/${slug}/brands?limit=18`).then(({ data }) => setBrands(data)).catch(() => {});
        api.get(`/offers?mall=${slug}&featured=true&limit=4`).then(({ data }) => setOffers(data)).catch(() => {});
        api.get(`/events?mall=${slug}&limit=3`).then(({ data }) => setEvents(data)).catch(() => {});
        api.get(`/malls/${slug}/services`).then(({ data }) => { setServices(data.services); setParking(data.parking); }).catch(() => {});
        api.get(`/malls/${slug}/floors`).then(({ data }) => setFloors(data)).catch(() => {});
        api.get(`/malls/${slug}/movies`).then(({ data }) => setMovies(data)).catch(() => {});
        api.get(`/malls/${slug}/restaurants`).then(({ data }) => setRestaurants(data)).catch(() => {});
        // Live parking refresh
        const t = setInterval(() => api.get(`/malls/${slug}/parking`).then(({ data }) => setParking(data)).catch(() => {}), 20000);
        return () => clearInterval(t);
    }, [slug, choose]);

    if (!mall) return <div className="min-h-screen flex items-center justify-center text-inorbit-blue">Loading…</div>;

    return (
        <div data-testid="mall-hub-page">
            {/* HERO */}
            <section className="relative h-[55vh] -mt-20 pt-20" data-testid="mall-hero">
                <img src={proxyImg(mall.hero_image)} alt={mall.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-inorbit-blue/20 via-inorbit-blue/40 to-inorbit-blue-deeper/95" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-12 text-white">
                    <div className="overline text-inorbit-yellow mb-2 flex items-center gap-3">
                        <MapPin size={12} /> {mall.city}, {mall.state}
                    </div>
                    <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none" style={{ color: 'white' }}>{mall.name}</h1>
                    <div className="script-tag text-inorbit-yellow text-3xl mt-3">Live an Experience</div>
                    <div className="mt-5 flex flex-wrap gap-6 text-sm text-white/85">
                        <span className="flex items-center gap-2"><Clock size={14} /> {mall.timings}</span>
                        <span className="flex items-center gap-2"><ShoppingBag size={14} /> {mall.brand_count}+ brands</span>
                        <span className="flex items-center gap-2"><Layers size={14} /> {mall.floors} floors</span>
                        {parking && <span className="flex items-center gap-2"><Car size={14} /> {parking.available?.toLocaleString('en-IN')} parking spots</span>}
                    </div>
                </div>
            </section>

            {/* AT-A-GLANCE BAR — quick stats + key actions */}
            <section className="bg-inorbit-yellow border-y-2 border-inorbit-blue/10" data-testid="mall-quickbar">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-6 text-inorbit-blue">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold">Now Open</div>
                            <div className="font-serif text-xl">{mall.timings}</div>
                        </div>
                        {parking && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold">Parking Available</div>
                                <div className="font-serif text-xl">{parking.available} / {parking.total}</div>
                            </div>
                        )}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold">Movies Today</div>
                            <div className="font-serif text-xl">{movies.length} films</div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link to={`/shop?mall=${mall.slug}`} className="btn-primary" data-testid="mall-shop-btn">
                            Shop {mall.short_name} <ArrowRight size={14} />
                        </Link>
                        <button onClick={() => setParkingModal(true)} className="btn-outline border-inorbit-blue text-inorbit-blue hover:bg-inorbit-blue hover:text-white" data-testid="mall-park-btn">
                            <Car size={14} /> Book Parking
                        </button>
                    </div>
                </div>
            </section>

            {/* SERVICES GRID */}
            {services.length > 0 && (
                <section className="py-20" data-testid="mall-services-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="overline mb-3">Mall Services</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-inorbit-blue mb-10 leading-tight">
                            Everything you need, <span className="script-tag text-inorbit-orange">on tap.</span>
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 stagger">
                            {services.map((s) => {
                                const Icon = ICONS[s.icon] || Sparkles;
                                return (
                                    <div key={s.id} className={`group p-5 rounded-md border border-border-subtle bg-white hover:border-inorbit-orange transition-all`} data-testid={`svc-${s.key}`}>
                                        <div className={`w-11 h-11 ${COLORS[s.color] || COLORS.blue} rounded-md flex items-center justify-center mb-3`}>
                                            <Icon size={20} strokeWidth={1.8} />
                                        </div>
                                        <div className="font-serif text-lg text-inorbit-blue group-hover:text-inorbit-orange leading-tight">{s.label}</div>
                                        <div className="text-xs text-text-secondary mt-2 leading-snug">{s.description}</div>
                                        <div className="text-[10px] tracking-widest uppercase text-inorbit-orange mt-3 font-semibold">{s.fee}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* PARKING + DIRECTIONS — side by side */}
            <section className="py-16 bg-cream" data-testid="mall-parking-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-6">
                    {/* PARKING CARD */}
                    {parking && (
                        <div className="bg-white border border-border-subtle rounded-md p-8 relative overflow-hidden" data-testid="parking-card">
                            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-inorbit-yellow/10" />
                            <div className="overline mb-3">Live Parking</div>
                            <div className="flex items-end justify-between mb-6">
                                <div>
                                    <div className="font-serif text-5xl text-inorbit-blue leading-none">{parking.available?.toLocaleString('en-IN')}</div>
                                    <div className="text-xs text-text-tertiary mt-1">spots available of {parking.total?.toLocaleString('en-IN')}</div>
                                </div>
                                <div className={`pill ${parking.occupancy_pct < 70 ? 'pill-yellow' : parking.occupancy_pct < 90 ? 'pill-orange' : 'pill-blue'}`}>
                                    {parking.occupancy_pct < 70 ? 'Plenty' : parking.occupancy_pct < 90 ? 'Filling fast' : 'Almost full'}
                                </div>
                            </div>
                            {/* Progress bar */}
                            <div className="h-2 bg-cream rounded-full overflow-hidden mb-6">
                                <div
                                    className="h-full bg-gradient-to-r from-inorbit-yellow to-inorbit-orange transition-all duration-700"
                                    style={{ width: `${parking.occupancy_pct}%` }}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-3 mb-6 text-sm">
                                <div><div className="text-text-tertiary text-[10px] uppercase tracking-widest">2 Hours</div><div className="font-serif text-xl text-inorbit-blue">₹{parking.rate_2h}</div></div>
                                <div><div className="text-text-tertiary text-[10px] uppercase tracking-widest">4 Hours</div><div className="font-serif text-xl text-inorbit-blue">₹{parking.rate_4h}</div></div>
                                <div><div className="text-text-tertiary text-[10px] uppercase tracking-widest">Full Day</div><div className="font-serif text-xl text-inorbit-blue">₹{parking.rate_full_day}</div></div>
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs text-text-tertiary mb-6">
                                <span className="pill pill-outline"><ShieldCheck size={10} /> {parking.covered_pct}% covered</span>
                                <span className="pill pill-outline"><Zap size={10} /> {parking.ev_bays} EV bays</span>
                                <span className="pill pill-outline"><Car size={10} /> Valet ₹{parking.valet_rate}</span>
                            </div>
                            <button onClick={() => setParkingModal(true)} className="btn-primary w-full justify-center" data-testid="book-parking-btn">
                                Book a Spot <ArrowRight size={14} />
                            </button>
                        </div>
                    )}

                    {/* DIRECTIONS CARD */}
                    <div className="bg-inorbit-blue text-white rounded-md p-8 relative overflow-hidden" data-testid="directions-card">
                        <div className="absolute -bottom-16 -left-16 w-48 h-48 border-2 border-inorbit-yellow/20 rounded-full" />
                        <div className="overline text-inorbit-yellow mb-3">Getting Here</div>
                        <h3 className="font-serif text-3xl leading-tight mb-4" style={{ color: 'white' }}>Directions to {mall.short_name}</h3>
                        <div className="text-sm text-white/90 leading-relaxed mb-6">{mall.address}</div>
                        <div className="space-y-2 text-sm text-white/80 mb-6">
                            <div className="flex items-start gap-3"><Clock size={14} className="mt-0.5 shrink-0" /> {mall.timings}</div>
                            <div className="flex items-start gap-3"><span className="shrink-0">📞</span> {mall.contact_phone}</div>
                            <div className="flex items-start gap-3"><span className="shrink-0">✉️</span> {mall.contact_email}</div>
                        </div>
                        <div className="flex flex-wrap gap-3 relative">
                            <a href={mall.google_maps_url} target="_blank" rel="noreferrer" className="btn-yellow" data-testid="directions-maps-btn">
                                <Navigation size={14} /> Open in Maps
                            </a>
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mall.address)}`} target="_blank" rel="noreferrer" className="btn-outline border-white text-white hover:bg-white hover:text-inorbit-blue">
                                Get Route
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* NOW SHOWING — MOVIES */}
            {movies.length > 0 && (
                <section className="py-20" data-testid="mall-movies-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="flex items-end justify-between gap-4 mb-10">
                            <div>
                                <div className="overline mb-3 flex items-center gap-2"><Film size={12} /> Cinema · PVR INOX</div>
                                <h2 className="font-serif text-4xl md:text-5xl text-inorbit-blue leading-tight">Now showing at <span className="script-tag text-inorbit-orange">{mall.short_name}</span></h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 stagger">
                            {movies.map((m) => (
                                <div key={m.id} className="group cursor-pointer" onClick={() => setMovieModal(m)} data-testid={`movie-${m.id}`}>
                                    <div className="aspect-[2/3] overflow-hidden rounded-md bg-cream relative">
                                        <img src={proxyImg(m.poster)} alt={m.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" loading="lazy" />
                                        <div className="absolute top-2 left-2 pill pill-yellow text-[10px]">{m.format}</div>
                                        <div className="absolute bottom-2 right-2 text-[10px] bg-black/60 backdrop-blur text-white px-2 py-1 rounded">⭐ {m.imdb_rating}</div>
                                    </div>
                                    <div className="mt-3">
                                        <div className="font-serif text-lg text-inorbit-blue leading-tight group-hover:text-inorbit-orange">{m.title}</div>
                                        <div className="text-[11px] text-text-tertiary uppercase tracking-wider mt-1">{m.lang} • {m.rating} • {m.duration_min}m</div>
                                        <div className="mt-2 text-xs text-inorbit-orange font-semibold flex items-center gap-1">
                                            <Ticket size={11} /> Book tickets
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* RESTAURANTS — Dining */}
            {restaurants.length > 0 && (
                <section className="py-20 bg-cream" data-testid="mall-restaurants-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="flex items-end justify-between gap-4 mb-10">
                            <div>
                                <div className="overline mb-3 flex items-center gap-2"><UtensilsCrossed size={12} /> Dining</div>
                                <h2 className="font-serif text-4xl md:text-5xl text-inorbit-blue leading-tight">Where to <span className="script-tag text-inorbit-orange">eat tonight</span></h2>
                            </div>
                            <Link to="/plan" className="btn-outline border-inorbit-orange text-inorbit-orange hover:bg-inorbit-orange hover:text-white" data-testid="ai-plan-cta">
                                <Wand2 size={14} /> Let AI Plan My Visit
                            </Link>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {restaurants.map((r) => (
                                <div key={r.id} onClick={() => setRestaurantModal(r)} className="card-luxury cursor-pointer group overflow-hidden" data-testid={`restaurant-${r.id}`}>
                                    <div className="aspect-[16/10] overflow-hidden bg-cream relative">
                                        <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                        <div className="absolute top-3 left-3 pill pill-yellow text-[10px]">{r.price_range}</div>
                                        <div className="absolute bottom-3 right-3 text-[10px] bg-black/60 backdrop-blur text-white px-2 py-1 rounded">⭐ {r.rating}</div>
                                    </div>
                                    <div className="p-5">
                                        <div className="font-serif text-xl text-inorbit-blue group-hover:text-inorbit-orange leading-tight">{r.name}</div>
                                        <div className="text-xs text-text-tertiary uppercase tracking-widest mt-1.5">{r.cuisine} · {r.floor}</div>
                                        <div className="text-sm text-text-secondary mt-2 italic">{r.vibe}</div>
                                        <div className="mt-3 flex items-baseline justify-between">
                                            <div className="font-semibold text-inorbit-blue">~ ₹{r.avg_per_person}<span className="text-xs text-text-tertiary"> / pp</span></div>
                                            <span className="text-xs text-inorbit-orange font-semibold flex items-center gap-1"><Ticket size={11} /> Book table</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FLOOR MAPS */}
            {floors.length > 0 && (
                <section className="py-20 bg-cream" data-testid="mall-floors-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="overline mb-3 flex items-center gap-2"><Layers size={12} /> Floor Directory</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-inorbit-blue mb-10 leading-tight">Explore <span className="script-tag text-inorbit-orange">every floor</span></h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                            {floors.map((f) => (
                                <div key={f.id} className="card-luxury p-7 group hover:scale-[1.02] transition-transform" data-testid={`floor-${f.floor}`}>
                                    <div className="flex items-baseline gap-3 mb-4">
                                        <div className="font-serif text-5xl text-inorbit-blue group-hover:text-inorbit-orange transition-colors">{f.floor}</div>
                                        <div className="text-xs uppercase tracking-widest text-text-tertiary">{f.label}</div>
                                    </div>
                                    <div className="text-sm text-text-secondary mb-4 leading-snug">{f.key_zones}</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {f.highlights.map((b) => (
                                            <span key={b} className="text-[10px] tracking-widest uppercase text-inorbit-blue bg-cream px-2 py-1 rounded">{b}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* TRENDING PRODUCTS */}
            {products.length > 0 && (
                <section className="py-20" data-testid="mall-products-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="flex items-end justify-between mb-10">
                            <div>
                                <div className="overline mb-3">Trending Now</div>
                                <h2 className="font-serif text-4xl text-inorbit-blue">Editor&rsquo;s picks at {mall.short_name}</h2>
                            </div>
                            <Link to={`/shop?mall=${mall.slug}`} className="btn-link">View all</Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 stagger">
                            {products.map((p) => (
                                <Link to={`/product/${p.id}`} key={p.id} className="card-luxury block img-zoom" data-testid={`mall-product-${p.id}`}>
                                    <div className="aspect-[4/5] overflow-hidden bg-cream">
                                        <img src={proxyImg(p.image || p.images?.[0])} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                    <div className="p-4">
                                        <div className="overline text-inorbit-orange text-[10px] mb-1 truncate">{p.brand}</div>
                                        <div className="text-sm text-inorbit-blue line-clamp-2 mb-2">{p.name}</div>
                                        <div className="font-semibold text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* BRANDS STRIP */}
            {brands.length > 0 && (
                <section className="py-20 bg-cream" data-testid="mall-brands-section">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="overline mb-3">Brands at {mall.short_name}</div>
                        <h2 className="font-serif text-4xl text-inorbit-blue mb-10">From everyday to luxury</h2>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
                            {brands.map((b) => (
                                <Link key={b.id} to={`/brands/${b.slug}`} className="card-luxury p-4 flex flex-col items-center text-center group">
                                    <div className="w-14 h-14 mb-2 flex items-center justify-center">
                                        <BrandLogo brand={b} className="max-w-full max-h-full object-contain" />
                                    </div>
                                    <div className="text-[11px] text-inorbit-blue font-medium leading-tight group-hover:text-inorbit-orange">{b.name}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* OFFERS + EVENTS */}
            {(offers.length > 0 || events.length > 0) && (
                <section className="py-20 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12">
                    {offers.length > 0 && (
                        <div data-testid="mall-offers-section">
                            <div className="overline mb-3">Live offers</div>
                            <h3 className="font-serif text-3xl text-inorbit-blue mb-8">Today at {mall.short_name}</h3>
                            <div className="space-y-3">
                                {offers.slice(0, 4).map((o) => (
                                    <Link to="/offers" key={o.id} className="flex items-center gap-4 border-b border-border-subtle py-3 group">
                                        <div className="w-16 h-16 overflow-hidden bg-cream shrink-0 rounded">
                                            <img src={proxyImg(o.image_url)} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="overline text-inorbit-orange text-[9px]">{o.brand_name}</div>
                                            <div className="text-sm text-inorbit-blue font-medium leading-tight group-hover:text-inorbit-orange line-clamp-1">{o.title}</div>
                                        </div>
                                        <ArrowRight size={14} className="text-text-tertiary group-hover:text-inorbit-orange shrink-0" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                    {events.length > 0 && (
                        <div data-testid="mall-events-section">
                            <div className="overline mb-3 flex items-center gap-2"><Calendar size={12} /> Happening Soon</div>
                            <h3 className="font-serif text-3xl text-inorbit-blue mb-8">Events at {mall.short_name}</h3>
                            <div className="space-y-3">
                                {events.map((e) => (
                                    <Link to="/events" key={e.id} className="flex items-center gap-4 border-b border-border-subtle py-3 group">
                                        <div className="w-16 h-16 overflow-hidden bg-cream shrink-0 rounded">
                                            <img src={proxyImg(e.image_url)} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="overline text-inorbit-orange text-[9px]">{e.event_type?.replace('_', ' ')}</div>
                                            <div className="text-sm text-inorbit-blue font-medium leading-tight group-hover:text-inorbit-orange line-clamp-1">{e.title}</div>
                                            <div className="text-[10px] text-text-tertiary mt-0.5">{new Date(e.start_datetime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                                        </div>
                                        <ArrowRight size={14} className="text-text-tertiary group-hover:text-inorbit-orange shrink-0" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* PARKING BOOKING MODAL */}
            <AnimatePresence>
                {parkingModal && <ParkingBookingModal mall={mall} parking={parking} onClose={() => setParkingModal(false)} requireAuth={type !== 'customer'} onBooked={(b) => { setParkingModal(false); setParking((p) => ({ ...p, available: Math.max(0, (p?.available || 0) - 1) })); toast.success(`Spot ${b.spot_label} reserved · ${b.qr_code}`); }} />}
            </AnimatePresence>

            {/* MOVIE BOOKING MODAL */}
            <AnimatePresence>
                {movieModal && <MovieBookingModal movie={movieModal} onClose={() => setMovieModal(null)} requireAuth={type !== 'customer'} onBooked={(b) => { setMovieModal(null); toast.success(`Booked ${b.seat_count} seat(s) · ${b.qr_code}`); }} />}
            </AnimatePresence>

            {/* RESTAURANT BOOKING MODAL */}
            <AnimatePresence>
                {restaurantModal && <TableBookingModal restaurant={restaurantModal} onClose={() => setRestaurantModal(null)} requireAuth={type !== 'customer'} onBooked={(b) => { setRestaurantModal(null); toast.success(`Table reserved at ${b.restaurant_name} · ${b.qr_code}`); }} />}
            </AnimatePresence>
        </div>
    );
}


function Modal({ children, onClose, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-inorbit-blue-deeper/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose} data-testid={testId}
        >
            <motion.div
                initial={{ scale: 0.96, y: 12, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="bg-white max-w-lg w-full max-h-[92vh] overflow-y-auto rounded-lg"
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </motion.div>
        </motion.div>
    );
}


function ParkingBookingModal({ mall, parking, onClose, requireAuth, onBooked }) {
    const [duration, setDuration] = useState('2h');
    const [vehicle, setVehicle] = useState('');
    const [loading, setLoading] = useState(false);
    const rate = { '2h': parking?.rate_2h, '4h': parking?.rate_4h, 'full_day': parking?.rate_full_day, 'valet': parking?.valet_rate }[duration];

    const submit = async () => {
        if (requireAuth) { toast.error('Please sign in to book parking'); return; }
        if (!vehicle.trim() || vehicle.length < 4) { toast.error('Enter a valid vehicle number'); return; }
        setLoading(true);
        try {
            const { data } = await api.post(`/malls/${mall.slug}/parking/book`, {
                duration, vehicle_number: vehicle.trim(),
            });
            onBooked(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Booking failed');
        } finally { setLoading(false); }
    };

    return (
        <Modal onClose={onClose} testId="parking-modal">
            <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div className="overline">Book Parking</div>
                        <h2 className="font-serif text-3xl text-inorbit-blue mt-1">{mall.short_name}</h2>
                    </div>
                    <button onClick={onClose} data-testid="parking-modal-close"><X size={22} /></button>
                </div>
                <div className="space-y-5">
                    <div>
                        <label className="overline mb-2 block">Vehicle Number</label>
                        <input value={vehicle} onChange={(e) => setVehicle(e.target.value.toUpperCase())} placeholder="MH 02 AB 1234" className="input-box w-full uppercase font-mono tracking-widest" maxLength={12} data-testid="parking-vehicle-input" />
                    </div>
                    <div>
                        <label className="overline mb-2 block">Duration</label>
                        <div className="grid grid-cols-2 gap-2">
                            {[['2h', '2 Hours', parking?.rate_2h], ['4h', '4 Hours', parking?.rate_4h], ['full_day', 'Full Day', parking?.rate_full_day], ['valet', 'Valet', parking?.valet_rate]].map(([k, l, r]) => (
                                <button key={k} onClick={() => setDuration(k)} className={`p-4 rounded-md border-2 text-left transition-all ${duration === k ? 'border-inorbit-orange bg-cream' : 'border-border-subtle hover:border-inorbit-blue'}`} data-testid={`parking-dur-${k}`}>
                                    <div className="text-[10px] uppercase tracking-widest text-text-tertiary">{l}</div>
                                    <div className="font-serif text-xl text-inorbit-blue mt-1">₹{r}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="bg-cream p-5 rounded-md flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-text-tertiary">Total</div>
                            <div className="font-serif text-3xl text-inorbit-blue mt-1">₹{rate}</div>
                        </div>
                        <div className="text-xs text-text-tertiary text-right">Mock payment — no real charge</div>
                    </div>
                    <button onClick={submit} disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="parking-confirm-btn">
                        {loading ? 'Booking…' : `Pay ₹${rate} & Confirm`} <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </Modal>
    );
}


function MovieBookingModal({ movie, onClose, requireAuth, onBooked }) {
    const [date, setDate] = useState(null);
    const [showtimeId, setShowtimeId] = useState(null);
    const [seats, setSeats] = useState(2);
    const [loading, setLoading] = useState(false);

    const dates = [...new Set((movie.showtimes || []).map((s) => s.date))].sort();
    useEffect(() => { if (!date && dates.length) setDate(dates[0]); }, [date, dates]);

    const slots = (movie.showtimes || []).filter((s) => s.date === date);
    const selected = slots.find((s) => s.id === showtimeId);
    const total = selected ? selected.price * seats : 0;

    const submit = async () => {
        if (requireAuth) { toast.error('Please sign in to book tickets'); return; }
        if (!selected) { toast.error('Pick a showtime'); return; }
        setLoading(true);
        try {
            const { data } = await api.post('/movies/book', { showtime_id: selected.id, seats });
            onBooked(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Booking failed');
        } finally { setLoading(false); }
    };

    return (
        <Modal onClose={onClose} testId="movie-modal">
            <div className="relative">
                <img src={proxyImg(movie.poster)} alt={movie.title} className="w-full aspect-[16/9] object-cover" />
                <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center" data-testid="movie-modal-close"><X size={18} /></button>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-5 text-white">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest mb-1">
                        <span className="pill pill-yellow text-[9px]">{movie.format}</span>
                        <span>{movie.lang}</span> · <span>{movie.rating}</span> · <span>{movie.duration_min}m</span>
                    </div>
                    <h2 className="font-serif text-3xl">{movie.title}</h2>
                </div>
            </div>
            <div className="p-7 space-y-5">
                <div className="text-sm text-text-secondary">{movie.synopsis}</div>
                <div>
                    <label className="overline mb-2 block">Select Date</label>
                    <div className="flex gap-2">
                        {dates.map((d) => {
                            const dt = new Date(d);
                            return (
                                <button key={d} onClick={() => { setDate(d); setShowtimeId(null); }} className={`flex-1 p-3 rounded-md border-2 text-center transition-all ${date === d ? 'border-inorbit-orange bg-cream' : 'border-border-subtle hover:border-inorbit-blue'}`} data-testid={`mov-date-${d}`}>
                                    <div className="text-[10px] uppercase tracking-widest text-text-tertiary">{dt.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                                    <div className="font-serif text-xl text-inorbit-blue">{dt.getDate()}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <label className="overline mb-2 block">Showtime</label>
                    <div className="flex flex-wrap gap-2">
                        {slots.map((s) => (
                            <button key={s.id} onClick={() => setShowtimeId(s.id)} className={`px-4 py-2 rounded-md border-2 text-xs font-semibold transition-all ${showtimeId === s.id ? 'border-inorbit-orange bg-inorbit-orange text-white' : 'border-border-subtle text-inorbit-blue hover:border-inorbit-blue'}`} data-testid={`mov-slot-${s.id}`}>
                                {s.time} <span className="opacity-60 ml-1">{s.screen}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="overline mb-2 block">Seats</label>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setSeats((n) => Math.max(1, n - 1))} className="w-10 h-10 rounded-md border-2 border-inorbit-blue text-inorbit-blue">−</button>
                        <div className="font-serif text-2xl text-inorbit-blue w-12 text-center" data-testid="mov-seats">{seats}</div>
                        <button onClick={() => setSeats((n) => Math.min(10, n + 1))} className="w-10 h-10 rounded-md border-2 border-inorbit-blue text-inorbit-blue">+</button>
                    </div>
                </div>
                {selected && (
                    <div className="bg-cream p-5 rounded-md flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-text-tertiary">Total</div>
                            <div className="font-serif text-3xl text-inorbit-blue mt-1">₹{total.toLocaleString('en-IN')}</div>
                        </div>
                        <div className="text-xs text-text-tertiary text-right">{seats} × ₹{selected.price}<br />Mock payment</div>
                    </div>
                )}
                <button onClick={submit} disabled={loading || !selected} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="movie-confirm-btn">
                    {loading ? 'Booking…' : selected ? `Pay ₹${total.toLocaleString('en-IN')} & Confirm` : 'Pick a showtime'} <ArrowRight size={14} />
                </button>
            </div>
        </Modal>
    );
}


function TableBookingModal({ restaurant, onClose, requireAuth, onBooked }) {
    const today = new Date();
    const dates = Array.from({ length: 5 }, (_, i) => {
        const d = new Date(today); d.setDate(d.getDate() + i); return d.toISOString().slice(0, 10);
    });
    const slots = ['12:30 PM', '1:30 PM', '7:00 PM', '7:30 PM', '8:30 PM', '9:30 PM'];
    const [date, setDate] = useState(dates[0]);
    const [time, setTime] = useState(slots[3]);
    const [party, setParty] = useState(2);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const total = restaurant.avg_per_person * party;

    const submit = async () => {
        if (requireAuth) { toast.error('Please sign in to book a table'); return; }
        setLoading(true);
        try {
            const { data } = await api.post(`/restaurants/${restaurant.id}/book`, { date, time, party_size: party, notes });
            onBooked(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Booking failed');
        } finally { setLoading(false); }
    };

    return (
        <Modal onClose={onClose} testId="table-modal">
            <div className="relative">
                <img src={restaurant.image} alt={restaurant.name} className="w-full aspect-[16/9] object-cover" />
                <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center" data-testid="table-modal-close"><X size={18} /></button>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-5 text-white">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest mb-1">
                        <span className="pill pill-yellow text-[9px]">{restaurant.price_range}</span>
                        <span>{restaurant.cuisine}</span> · <span>⭐ {restaurant.rating}</span>
                    </div>
                    <h2 className="font-serif text-3xl">{restaurant.name}</h2>
                </div>
            </div>
            <div className="p-7 space-y-5">
                <div className="text-sm text-text-secondary italic">{restaurant.vibe} · {restaurant.ambience}</div>
                <div>
                    <label className="overline mb-2 block">Select Date</label>
                    <div className="flex gap-2 overflow-x-auto">
                        {dates.map((d) => {
                            const dt = new Date(d);
                            return (
                                <button key={d} onClick={() => setDate(d)} className={`shrink-0 p-3 rounded-md border-2 text-center w-20 transition-all ${date === d ? 'border-inorbit-orange bg-cream' : 'border-border-subtle hover:border-inorbit-blue'}`} data-testid={`tab-date-${d}`}>
                                    <div className="text-[10px] uppercase tracking-widest text-text-tertiary">{dt.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                                    <div className="font-serif text-xl text-inorbit-blue">{dt.getDate()}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <label className="overline mb-2 block">Time</label>
                    <div className="flex flex-wrap gap-2">
                        {slots.map((s) => (
                            <button key={s} onClick={() => setTime(s)} className={`px-4 py-2 rounded-md border-2 text-xs font-semibold transition-all ${time === s ? 'border-inorbit-orange bg-inorbit-orange text-white' : 'border-border-subtle text-inorbit-blue hover:border-inorbit-blue'}`} data-testid={`tab-slot-${s.replace(/[^0-9A-Z]/gi, '')}`}>
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="overline mb-2 block">Party size</label>
                        <div className="flex items-center gap-3">
                            <button onClick={() => setParty((n) => Math.max(1, n - 1))} className="w-10 h-10 rounded-md border-2 border-inorbit-blue text-inorbit-blue">−</button>
                            <div className="font-serif text-2xl text-inorbit-blue w-10 text-center" data-testid="tab-party">{party}</div>
                            <button onClick={() => setParty((n) => Math.min(20, n + 1))} className="w-10 h-10 rounded-md border-2 border-inorbit-blue text-inorbit-blue">+</button>
                        </div>
                    </div>
                    <div>
                        <label className="overline mb-2 block">Special note</label>
                        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="window seat, allergies…" className="input-box w-full" data-testid="tab-notes" />
                    </div>
                </div>
                <div className="bg-cream p-5 rounded-md flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-text-tertiary">Estimated total</div>
                        <div className="font-serif text-3xl text-inorbit-blue mt-1">₹{total.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="text-xs text-text-tertiary text-right">{party} × ₹{restaurant.avg_per_person}<br />Reservation only — pay at restaurant</div>
                </div>
                <button onClick={submit} disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="table-confirm-btn">
                    {loading ? 'Reserving…' : 'Reserve Table'} <ArrowRight size={14} />
                </button>
            </div>
        </Modal>
    );
}
